import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Mail, Shield, Building } from "lucide-react";
import AvatarUploader from "@/components/AvatarUploader";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "", birth_date: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "", phone: user.phone || "", address: user.address || "",
      birth_date: user.birth_date || "", password: "",
    });
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await api.put(`/users/${user.id}`, payload);
      await refresh();
      toast.success("Profil aktualisiert");
      setForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="ph-label">Konto</p>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Mein Profil</h1>
        <p className="text-slate-500 text-sm mt-1">Persönliche Daten und Passwort.</p>
      </div>

      <div className="ph-card p-6 flex items-center gap-6">
        <AvatarUploader user={user} editable={true} size="xl" onChange={refresh} />
        <div className="space-y-1">
          <div className="font-display text-2xl font-semibold">{user.name}</div>
          <div className="text-sm text-slate-600 inline-flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {user.email}</div>
          <div className="text-sm text-slate-600 inline-flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> {user.role === "admin" ? "Chef / Admin" : "Mitarbeiter"}</div>
          {user.position && <div className="text-sm text-slate-600 inline-flex items-center gap-2"><Building className="h-3.5 w-3.5" /> {user.position}{user.department && ` · ${user.department}`}</div>}
        </div>
      </div>

      <div className="ph-card p-6 space-y-4">
        <h2 className="font-display text-lg font-semibold">Persönliche Daten</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Telefon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Adresse"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <Field label="Geburtsdatum"><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></Field>
          <Field label="Neues Passwort"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="leer = nicht ändern" /></Field>
        </div>
        <div className="flex justify-end">
          <Button data-testid="btn-save-profile" onClick={save} disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
